import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  EventEmitter,
  inject,
  Inject,
  Injector,
  input,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild,
  ViewContainerRef,
  WritableSignal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatTableModule } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { RxState } from '@rx-angular/state';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { GalaxySnackbarModule, SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { GalaxyDataSource, GalaxyTableModule, Row } from '@vendasta/galaxy/table';
import { GalaxyColumnDef } from '@vendasta/galaxy/table/src/table.interface';
import {
  BehaviorSubject,
  combineLatest,
  firstValueFrom,
  forkJoin,
  Observable,
  of,
  ReplaySubject,
  Subscription,
} from 'rxjs';
import {
  ActivityType,
  CrmDependencies,
  CrmInjectionToken,
  CrmMultiLocationDependencies,
  CrmMultilocationInjectionToken,
  CrmObjectDependencies,
  CrmObjectInjectionToken,
  CRMRowObject,
  CRMTableSelection,
  ListObjectView,
  MultiRowAction,
  ObjectType,
  SelectAllAction,
  SingleRowAction,
} from '../../tokens-and-interfaces';
import { showActionsMenu, showSelectAllAffordance, showSelectionColumn, TableSelectionVisibility } from './picker-mode';
import { LIST_OBJECTS_DEFAULT_PAGE_SIZE, LIST_OBJECTS_PAGE_SIZE_OPTIONS, PAGE_ROUTES } from '../../constants';
import { TranslationModule } from '../../i18n/translation-module';
import { ListObjectsTableService } from './list-objects-table.service';
import {
  bufferTime,
  catchError,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { SidepanelComponent } from '../side-panel/side-panel.component';
import { FilterVisibleActionsPipe } from '../../shared-pipes';
import { CrmTableStateService } from '../../shared-services/crm-services/crm-object-service/crm-table-state.service';
import {
  GalaxyFilterChipDependencies,
  GalaxyFilterChipInjectionToken,
  GalaxyFilterChipsModule,
  GalaxyFilterInterface,
  GalaxyFilterOperator,
} from '@vendasta/galaxy/filter/chips';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { CrmFiltersService } from '../../shared-services/crm-services/crm-filters.service';
import { decodeFilters, encodeFilters, validateFilters } from '@vendasta/galaxy/filter/chips/src/utils';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { PresetFilters, SavedFilter } from '../filter-view';
import { ObjectWorkspaceViewFilterService } from '../../object-workspace';
import { GalaxyFilterChipsComponent } from '@vendasta/galaxy/filter/chips/src/galaxy-filter-chips.component';
import { CustomRowStyle } from '../../shared-services/table-customization/_abstract-table-customization.service';
import { batchGetStatus$ } from '@vendasta/protos/partner/v1/feature-flags';
import {
  ContactCustomColumns,
  CrmPipelineService,
  CrmViewConfigService,
  CRMTrackingService,
  Pipeline,
  StandardIds,
} from '../../shared-services';
import { FadeIn, LoadingBehavior } from '@galaxy/crm/components/animations';
import {
  CrmBoardCardContentDirective,
  CrmBoardColumnHeaderDirective,
  CrmBoardComponent,
  CrmBoardDatasourceInterface,
  CrmBoardItemClickedEvent,
  CrmBoardItemClickedToken,
  CrmBoardItemCompareToken,
} from '@galaxy/crm/components/board';
import { ListObjectsBoardColumnHeaderComponent } from './board/column-header.component';
import { ListObjectsBoardCardContentComponent } from './board/card-content.component';
import { PipelineSelectorComponent } from '../pipeline-selector';
import { GalaxyTableSelectionCustomization, GalaxyTableSelectionCustomizationToken } from '@vendasta/galaxy/table/';
import { ValueSummary, ValueSummaryService } from './value-summary.service';
import { TranslateForCrmObjectPipe, TranslateForCrmObjectService } from '../../i18n';
import { TabGroup, ViewTabsComponent } from '@galaxy/crm/components/view-tabs';
import { TranslateService } from '@ngx-translate/core';
import { ViewConfigurationsApiService } from '@vendasta/crm';

@Component({
  selector: 'crm-list-objects-table',
  templateUrl: `./list-objects-table.component.html`,
  styleUrls: ['./list-objects-table.component.scss'],
  imports: [
    CommonModule,
    GalaxyEmptyStateModule,
    GalaxyLoadingSpinnerModule,
    GalaxyTableModule,
    MatTableModule,
    MatIconModule,
    MatMenuModule,
    MatButtonModule,
    TranslationModule,
    MatDialogModule,
    GalaxySnackbarModule,
    GalaxyBadgeModule,
    GalaxyPageModule,
    SidepanelComponent,
    FilterVisibleActionsPipe,
    GalaxyFilterChipsModule,
    MatSortModule,
    CrmBoardComponent,
    CrmBoardColumnHeaderDirective,
    CrmBoardCardContentDirective,
    ListObjectsBoardColumnHeaderComponent,
    ListObjectsBoardCardContentComponent,
    PipelineSelectorComponent,
    TranslateForCrmObjectPipe,
    ViewTabsComponent,
  ],
  animations: [LoadingBehavior, FadeIn],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    ListObjectsTableService,
    CrmTableStateService,
    RxState,
    {
      provide: CrmBoardItemCompareToken,
      useValue: (a: Row, b: Row) => a.id === b.id,
    },
    {
      provide: CrmBoardItemClickedToken,
      useValue: (r: Row): CrmBoardItemClickedEvent => {
        return { objectId: r.id };
      },
    },
    {
      provide: GalaxyTableSelectionCustomizationToken,
      useValue: {
        rowsSelectionLimit: 100,
      } as GalaxyTableSelectionCustomization,
    },
  ],
})
export class ListObjectsTableComponent implements OnInit, OnDestroy {
  protected readonly view: WritableSignal<'grid' | 'list'> = signal('list');
  protected readonly selectedPipeline: WritableSignal<Pipeline | undefined> = signal(undefined);
  protected readonly initialPipeline: WritableSignal<string> = signal('');
  protected readonly isBoardView = computed(() => {
    const view = this.view();
    return view === 'grid';
  });
  private readonly isBoardView$ = toObservable(this.isBoardView);
  protected readonly isOnBoardFeatureFlag$: Observable<boolean>;
  protected readonly showBoardEmptyState = signal(false);
  protected readonly isMultiLocation$: Observable<boolean>;

  protected readonly boardColumnLayout = computed(() =>
    this.service.defaultBoardColumns(
      this._objectType(),
      this.activityType(),
      this.selectedPipeline(),
      this.crmObjectSubtype(),
    ),
  );

  @Input() rowStyle: CustomRowStyle | undefined;

  private readonly multiActions$$ = new BehaviorSubject<MultiRowAction[]>([]);

  @Input() set multiActions(actions: MultiRowAction[]) {
    this.multiActions$$.next(actions);
  }

  private readonly singleActions$$ = new BehaviorSubject<SingleRowAction[]>([]);

  @Input() set singleActions(actions: SingleRowAction[]) {
    this.singleActions$$.next(actions);
  }

  private readonly injector = inject(Injector);

  private readonly crmObjectDependencies: CrmObjectDependencies | null = inject(CrmObjectInjectionToken, {
    optional: true,
  });

  // Present only inside the workspace shell; lets a shell-hosted views sidebar drive this table.
  private readonly workspaceViewFilters = inject(ObjectWorkspaceViewFilterService, { optional: true });

  // Where tickets get their filter chips from. CrmFiltersService only knows the object types it
  // was written for; the rest bring their own chips through this token. See viewFilterSeeds().
  //
  // Resolved on demand rather than in a field initializer: a chip token can capture observables from
  // the services around it the moment it is built, so building it before the table needs it changes
  // what those observables see.
  private get filterChipDependencies(): GalaxyFilterChipDependencies | null {
    return this.injector.get(GalaxyFilterChipInjectionToken, null);
  }

  crmObjectSubtype = input<string>('');
  tableStateKey = input<string>('');
  baseColumnIdsOverride = input<string[]>([]);
  private readonly _objectType = signal<ObjectType>('Contact');
  protected readonly objectType$ = toObservable(this._objectType);
  protected readonly showPipelineSettings$ = this.objectType$.pipe(map((objectType) => objectType === 'Opportunity'));

  @Input({ required: true }) set objectType(o: ObjectType) {
    this._objectType.set(o);
    this.tableStateService.setLocalStorageKey(this._objectType(), this.tableStateKey() || this.crmObjectSubtype());
    this.service.setObjectType(this._objectType());
    this.summaryService.setObjectType(this._objectType());
    this.loadFilterBarState();
  }

  get objectType(): ObjectType {
    return this._objectType();
  }

  get objectTypeForTranslation(): ObjectType | ActivityType {
    return this.activityType() ?? this.objectType;
  }

  protected readonly showDefaultRowActions = computed(() => this._objectType() !== 'PotentialContact');

  get totalDataMembers(): number {
    return this.dataSource.state.totalDataMembers ?? 0;
  }

  readOnlyMode$$ = new BehaviorSubject(false);
  isReadOnlyMode = toSignal(this.readOnlyMode$$);

  readonly filterBarOpen = signal<boolean | null>(null);

  //readonly tables don't have filters by default, but if override is turned on the filters will show 
  protected readonly filtersToggleable = computed(() => !this.isReadOnlyMode() || this.showPresetFiltersOverride());

  readonly showFiltersOpen = computed(
    () => this.filtersToggleable() && (this.filterBarOpen() ?? !this.isReadOnlyMode()),
  );

  @Input() set readOnlyMode(value: boolean) {
    this.readOnlyMode$$.next(value ?? false);
    this.tableStateService?.setReadOnly?.(value ?? false);
  }

  // Inside the workspace shell the views sidebar hosts the view tabs, so suppress the inline ones.
  readonly hideViewTabs = computed(() => this.selectionMode() || (this.workspaceViewFilters?.active() ?? false));

  filtersAreForced = false;

  @Input() set forceFilters(filters: GalaxyFilterInterface[]) {
    if (filters && filters.length > 0) {
      this.filtersAreForced = true;
      this.setViewFilters([...filters], true);
    }
  }

  protected readonly multiRowActions$: Observable<MultiRowAction[]> = this.objectType$.pipe(
    distinctUntilChanged(),
    switchMap((objectType) => {
      return this.createMultiRowActions(objectType);
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  protected readonly selectAllActions$: Observable<SelectAllAction[]> = this.objectType$.pipe(
    distinctUntilChanged(),
    switchMap((objectType) => {
      return this.createSelectAllActions(objectType);
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  protected readonly singleRowActions$: Observable<SingleRowAction[]> = this.objectType$.pipe(
    switchMap((objectType) => {
      return this.createSingleRowActions(objectType);
    }),
  );
  activityType = input<ActivityType | undefined>();

  presetFilters = input<PresetFilters>();
  showPresetFiltersOverride = input<boolean>(false);
  showViewTabsOverride = input<boolean>(false);
  hideCustomViews = input<boolean>(false);
  selectionMode = input<boolean>(false);
  @Input() refreshRow$?: Observable<string>;

  @Output() editClick: EventEmitter<CRMRowObject> = new EventEmitter<CRMRowObject>();
  @Output() selected: EventEmitter<Row[]> = new EventEmitter<Row[]>();
  @Output() pickerSelectionChange = new EventEmitter<CRMTableSelection>();
  @Output() pipelineSelected = new EventEmitter<boolean>();
  @Output() currentView = new EventEmitter<ListObjectView>();

  @ViewChild('filterChipRef') filterChipRef?: GalaxyFilterChipsComponent;
  @ViewChild('automationsSideMenuContainer', {
    static: true,
    read: ViewContainerRef,
  })
  automationsSideMenuContainer?: ViewContainerRef;

  protected readonly dataSource: GalaxyDataSource<Row, GalaxyFilterInterface, MatSort> = this.service.dataSource;
  protected readonly boardSource: CrmBoardDatasourceInterface<Row, GalaxyFilterInterface, MatSort> =
    this.service.boardSource;
  protected readonly columns$: Observable<GalaxyColumnDef[]> = combineLatest([
    this.service.columns$,
    this.readOnlyMode$$,
    toObservable(this.selectionMode),
  ]).pipe(
    map(([columns, isReadOnlyMode, selectionMode]) => {
      if (isReadOnlyMode || selectionMode) {
        return columns.filter((c) => c.id !== 'actions');
      }
      return columns;
    }),
  );
  protected readonly groupConfigs$: Observable<GalaxyColumnDef[]> = this.service.groupConfigs$;
  protected readonly showEmptyState$: Observable<boolean> = combineLatest([
    this.service.showEmptyState$,
    this.isBoardView$,
  ]).pipe(map(([showEmptyState, isBoardView]) => showEmptyState && !isBoardView && !this.filtersAreForced));
  protected selectedRows: Row[] = [];

  protected readonly filters = signal<GalaxyFilterInterface[]>([]);
  protected readonly showFiltersApplied$: Observable<boolean> = combineLatest([
    this.dataSource.filtersApplied$,
    toObservable(this.filters),
  ]).pipe(
    map(
      ([filtersApplied, filters]) =>
        filtersApplied &&
        filters.filter(
          (f) =>
            !!f.values?.length ||
            f.operator === GalaxyFilterOperator.FILTER_OPERATOR_IS_NOT_EMPTY ||
            f.operator === GalaxyFilterOperator.FILTER_OPERATOR_IS_EMPTY,
        ).length > 0,
    ),
  );

  protected readonly pageSizeOptions: number[] = LIST_OBJECTS_PAGE_SIZE_OPTIONS;
  protected readonly pageSize: number = LIST_OBJECTS_DEFAULT_PAGE_SIZE;

  private readonly confirmationModalService = inject(OpenConfirmationModalService);
  private readonly crmPipelineService = inject(CrmPipelineService);

  protected tableId = '';

  private readonly subscriptions: Subscription[] = [];

  protected readonly useSelectAll$ = this.dataSource.selectAll$.pipe(
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  protected readonly multiRowActions = toSignal(this.multiRowActions$, { initialValue: [] });
  protected readonly selectAllActions = toSignal(this.selectAllActions$, { initialValue: [] });
  protected readonly useSelectAll = toSignal(this.useSelectAll$, { initialValue: false });

  protected readonly activeMenuActions = computed(() => {
    const useSelectAll = this.useSelectAll();
    if (useSelectAll) {
      return this.selectAllActions().map((action) => ({
        label: action.label,
        isSelectAll: true as const,
        callback: (rows: Row[]) => {
          action.callback(rows, {
            filters: this.filters(),
            search: this.dataSource.state.searchTerm,
            useSelectAll: this.dataSource.state.selectAll,
            totalObjects: this.dataSource.state.totalDataMembers,
          });
        },
      }));
    } else {
      return this.multiRowActions().map((action) => ({
        label: action.label,
        isSelectAll: false as const,
        callback: (rows: Row[]) => action.callback(rows),
      }));
    }
  });

  private readonly selectionVisibility = computed<TableSelectionVisibility>(() => ({
    selectionMode: this.selectionMode(),
    hasMultiActions: this.multiRowActions().length > 0,
    isBoardView: this.isBoardView(),
    readOnly: this.isReadOnlyMode() ?? false,
    hasSelectAllActions: this.selectAllActions().length > 0,
  }));

  protected readonly showSelection = computed(() => showSelectionColumn(this.selectionVisibility()));
  protected readonly showActionsMenu = computed(() => showActionsMenu(this.selectionVisibility()));
  protected readonly showSelectAll = computed(() => showSelectAllAffordance(this.selectionVisibility()));

  protected readonly initialLoadingCompleted$ = this.service.initialLoadingCompleted$;

  protected readonly boardValueSummary$: Observable<ValueSummary> = this.summaryService.valueSummary$;

  constructor(
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    @Inject(CrmMultilocationInjectionToken) private readonly multiLocationConfig: CrmMultiLocationDependencies,
    private readonly service: ListObjectsTableService,
    private readonly router: Router,
    private readonly snackService: SnackbarService,
    protected readonly dialog: MatDialog,
    private readonly tableStateService: CrmTableStateService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly crmFiltersService: CrmFiltersService,
    private readonly trackingService: CRMTrackingService,
    private readonly summaryService: ValueSummaryService,
    private readonly translateForCrmObjectService: TranslateForCrmObjectService,
    private readonly translate: TranslateService,
    private readonly viewConfigService: ViewConfigurationsApiService,
    private readonly crmViewConfigService: CrmViewConfigService,
  ) {
    this.setUpViewFilterEffects();
    effect(() => {
      const subtype = this.crmObjectSubtype();
      this.service.setObjectSubtype(subtype || '');
    });
    effect(() => {
      const activityType = this.activityType();
      if (activityType) {
        this.service.setActivityType(activityType);
      }
    });
    effect(() => {
      // Publish current filters so a workspace sidebar can seed a new view from them.
      this.workspaceViewFilters?.setCurrentFilters(this.filters());
    });

    this.isMultiLocation$ = this.multiLocationConfig.isMultiLocation$ ?? of(false);
    this.activatedRoute.data
      .pipe(
        map((data) => (data?.['view'] === 'board' ? 'grid' : 'list')),
        take(1),
      )
      .subscribe((routeView: 'grid' | 'list') => {
        this.view.set(routeView);
      });
    this.isOnBoardFeatureFlag$ = combineLatest([
      this.config.namespace$,
      this.config.parentNamespace$,
      this.objectType$,
    ]).pipe(
      switchMap(([namespace, parentNamespace, objectType]) => {
        if (objectType === 'Opportunity') {
          return of(true);
        }
        if (objectType === 'CustomObject') {
          return this.config.hasCustomObjectPipelineFeatureFlag$ ?? of(false);
        }
        if (objectType === 'Company' || objectType === 'Contact') {
          return batchGetStatus$({
            featureIds: ['crm_contact_company_board_view'],
            partnerId: (parentNamespace as string) || (namespace as string),
          }).pipe(
            map((response) => {
              return response?.hasAccess.length > 0 && response?.hasAccess[0];
            }),
          );
        }
        return of(true);
      }),
      startWith(false),
    );
    this.dataSource.clearSelectionOnChange = true;
    const filterParam = this.activatedRoute.snapshot.queryParamMap.get('filter');
    let filters: GalaxyFilterInterface[] = [];
    if (filterParam) {
      const f = decodeFilters(filterParam);
      if (validateFilters(f) && f.length > 0) {
        filters = f;
      }
    }

    const pipelineId = this.activatedRoute.snapshot.queryParamMap.get('pipelineId');
    if (pipelineId) {
      const decodedPipelineId = decodeURIComponent(pipelineId);
      this.initialPipeline.set(decodedPipelineId);
      this.crmPipelineService.setCurrentPipelineId(decodedPipelineId);
    }

    if (this.isBoardView()) {
      const pipelineFieldIds = [StandardIds.ActivityTaskStatus, StandardIds.OpportunityPipelineID];
      const pipelineFilterIndex = filters.findIndex((filter) => pipelineFieldIds.includes(filter.fieldId));
      if (pipelineFilterIndex > -1) {
        filters.splice(pipelineFilterIndex, 1);
      }
    } else {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { pipelineId: null },
        queryParamsHandling: 'merge',
      });
    }
    this.crmFiltersService.setInitialFilters(filters);
    this.boardSource.setFilters(filters);

    this.subscriptions.push(
      this.boardSource.filters$.subscribe((boardFilters) => this.summaryService.setFilters(boardFilters)),
    );

    const sub = combineLatest([this.activatedRoute.queryParams, this.dataSource.state$])
      .pipe(
        tap(([queryParams, state]) => {
          const newQueryParams = { ...queryParams };
          let filterParam: string | null = null;
          if (state.filters && state.filters.length > 0) {
            const readyFilters = state.filters.filter(
              (filter) => !(filter.operator === GalaxyFilterOperator.FILTER_OPERATOR_IS && filter.values?.length === 0),
            );
            if (readyFilters.length > 0) {
              filterParam = encodeFilters(readyFilters);
              newQueryParams['filter'] = filterParam;
            } else {
              delete newQueryParams['filter'];
            }
          } else {
            delete newQueryParams['filter'];
          }
          // Note: `queryParamsHandling: 'merge'` doesn't work when multiple shared-components are adjusting the query params
          // at the same time. Without this other shared-components may not be able to write to the query params.
          if (queryParams['filter'] === newQueryParams['filter']) {
            return;
          }
          if (this.filtersAreForced) {
            return;
          }
          this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: newQueryParams,
          });
        }),
      )
      .subscribe();

    this.subscriptions.push(sub);

    const currentView = this.isBoardView$.subscribe((isBoardView) => {
      const view = isBoardView ? 'board' : 'table';
      this.currentView.emit(view);
    });

    this.subscriptions.push(currentView);
    effect(() => {
      const sortingChanged = this.dataSource.sortingChanged();
      sortingChanged.forEach((sort) => {
        this.trackingService.trackEvent(this.objectType, 'sorted-crm-object-table', {
          columnId: sort.active,
          direction: sort.direction,
          objectType: this.objectType,
        });
      });
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  ngOnInit() {
    this.tableStateService.setLocalStorageKey(this._objectType(), this.tableStateKey() || this.crmObjectSubtype());

    const columnsOverride = this.baseColumnIdsOverride();
    if (columnsOverride.length > 0) {
      this.service.setBaseColumns(columnsOverride);
    } else if (this.crmObjectDependencies) {
      if (this.crmObjectDependencies.baseColumnIds && this.crmObjectDependencies.baseColumnIds.length > 0) {
        this.service.setBaseColumns(this.crmObjectDependencies.baseColumnIds);
      }
    } else {
      switch (this.objectType) {
        case 'Contact':
          if (this.config.contact?.baseColumnIds && this.config.contact.baseColumnIds.length > 0) {
            this.service.setBaseColumns(this.config.contact.baseColumnIds);
          }
          break;
        case 'Company':
          if (this.config.company?.baseColumnIds && this.config.company.baseColumnIds.length > 0) {
            this.service.setBaseColumns(this.config.company.baseColumnIds);
          }
          break;
        case 'Activity':
          switch (this.activityType()) {
            case 'Task':
              if (this.config.task?.baseColumnIds && this.config.task.baseColumnIds.length > 0) {
                this.service.setBaseColumns(this.config.task.baseColumnIds);
              }
              break;
          }
          break;
        case 'Opportunity':
          if (this.config.opportunity?.baseColumnIds && this.config.opportunity.baseColumnIds.length > 0) {
            this.service.setBaseColumns(this.config.opportunity.baseColumnIds);
          }
          break;
      }
    }

    this.tableId = this.config.appID + '-' + this.objectType;

    if (this.workspaceViewFilters) {
      this.subscriptions.push(
        this.workspaceViewFilters.selectedFilters$.subscribe((filters) => {
          if (filters) {
            this.applyViewFilters(filters);
          }
        }),
      );
    }

    if (this.refreshRow$) {
      this.subscriptions.push(
        this.refreshRow$
          .pipe(
            bufferTime(1000),
            map((rows) => rows.filter((row) => Boolean(row))),
            filter((rows) => rows.length > 0),
          )
          .subscribe((rowsToRefresh) => {
            this.service.refreshRows(rowsToRefresh);
          }),
      );
    }
    if (this.config.automationsSideMenuComponent) {
      this.config
        .automationsSideMenuComponent()
        .then((component) => this.automationsSideMenuContainer?.createComponent(component))
        .catch((err) => console.warn('automations side menu unavailable', err));
    }
  }

  protected editObject(row: Row): void {
    const namespace = row?.data['namespace']?.value ?? '';
    this.editClick.emit({
      namespace: namespace,
      objectId: row.id,
      objectType: this.objectType,
    });

    const routePrefix$ = this.isMultiLocation$.pipe(
      switchMap((isMultiLocation) => {
        return isMultiLocation ? this.multiLocationConfig.routePrefix$ : this.config.routePrefix$;
      }),
    );
    combineLatest([routePrefix$, this.crmObjectDependencies?.defaultPageUrl$ ?? of(''), this.isMultiLocation$])
      .pipe(take(1))
      .subscribe(([routePrefix, defaultPageUrl, isMultiLocation]) => {
        let editRouteTemplate;
        if (this.crmObjectDependencies) {
          editRouteTemplate = `${defaultPageUrl}/${PAGE_ROUTES.CUSTOM_OBJECT.SUBROUTES.EDIT}`;
        } else {
          switch (this.objectType) {
            case 'Contact':
              if (isMultiLocation) {
                editRouteTemplate = `${routePrefix}/${PAGE_ROUTES.MULTI_LOCATION.CONTACTS.ROOT}/${PAGE_ROUTES.MULTI_LOCATION.CONTACTS.SUBROUTES.EDIT}`;
              } else {
                editRouteTemplate = `${routePrefix}/${PAGE_ROUTES.CONTACT.ROOT}/${PAGE_ROUTES.CONTACT.SUBROUTES.EDIT}`;
              }
              break;
            case 'Company':
              editRouteTemplate = `${routePrefix}/${PAGE_ROUTES.COMPANY.ROOT}/${PAGE_ROUTES.COMPANY.SUBROUTES.EDIT}`;
              break;
            case 'Opportunity':
              editRouteTemplate = `${routePrefix}/${PAGE_ROUTES.OPPORTUNITY.ROOT}/${PAGE_ROUTES.OPPORTUNITY.SUBROUTES.EDIT}`;
              break;
          }
        }
        if (editRouteTemplate) {
          const editRoute = editRouteTemplate.replace(':crmObjectId', row.id).replace(':namespace', namespace);
          this.router.navigateByUrl(editRoute);
        }
      });
  }

  protected deleteObject(row: Row): void {
    let name: string;
    switch (this.objectTypeForTranslation) {
      case 'Company':
        name = (row?.data?.[StandardIds.CompanyName]?.value as string) || 'company';
        break;
      case 'Contact':
        name = (row?.data?.[ContactCustomColumns.FullName]?.value as string) || 'contact';
        break;
      case 'Task':
        name = (row?.data?.[StandardIds.ActivityName]?.value?.name as string) || 'task';
        break;
      default:
        name = '';
        break;
    }

    combineLatest([
      this.translateForCrmObjectService.getTranslationForCrmObject(
        this.objectTypeForTranslation,
        'LIST_OBJECTS_TABLE.ACTIONS.DELETE.TITLE',
        { name: name },
      ),
      this.translateForCrmObjectService.getTranslationForCrmObject(
        this.objectTypeForTranslation,
        'LIST_OBJECTS_TABLE.ACTIONS.DELETE.CONFIRMATION_MESSAGE',
      ),
      this.translateForCrmObjectService.getTranslationForCrmObject(
        this.objectTypeForTranslation,
        'LIST_OBJECTS_TABLE.ACTIONS.DELETE_SELECTED.CONFIRM',
      ),
      this.translateForCrmObjectService.getTranslationForCrmObject(
        this.objectTypeForTranslation,
        'LIST_OBJECTS_TABLE.ACTIONS.DELETE_SELECTED.CANCEL',
      ),
      this.translateForCrmObjectService.getTranslationForCrmObject(
        this.objectTypeForTranslation,
        'LIST_OBJECTS_TABLE.ACTIONS.DELETE.CONFIRMATION_TEXT',
      ),
      this.translateForCrmObjectService.getTranslationForCrmObject(
        this.objectTypeForTranslation,
        'LIST_OBJECTS_TABLE.ACTIONS.DELETE.INSTRUCTIONS',
      ),
    ])
      .pipe(
        switchMap(([title, message, confirmationButtonText, cancelButtonText, confirmationText, instructions]) => {
          return this.confirmationModalService.openModal({
            type: 'warn',
            title: title,
            message: message + '\n' + instructions,
            confirmButtonText: confirmationButtonText,
            cancelButtonText: cancelButtonText,
            confirmationText: confirmationText || undefined,
          });
        }),
        withLatestFrom(
          this.translateForCrmObjectService.getTranslationForCrmObject(
            this.objectTypeForTranslation,
            'LIST_OBJECTS_TABLE.ACTIONS.DELETE.SUCCESS',
          ),
          this.translateForCrmObjectService.getTranslationForCrmObject(
            this.objectTypeForTranslation,
            'LIST_OBJECTS_TABLE.ACTIONS.DELETE.ERROR',
          ),
        ),
        take(1),
      )
      .subscribe(([confirmed, success, error]: [boolean, string, string]) => {
        if (confirmed) {
          this.service.deleteObject(row.data['namespace']?.value ?? '', row.id).subscribe({
            next: () => {
              this.snackService.openSuccessSnack(success);
            },
            error: (err) => {
              console.error(err);
              this.snackService.openErrorSnack(error);
            },
          });
        }
      });
  }

  protected viewObject(objectId: string): void {
    if (!objectId) {
      console.error('ObjectID required when navigating to a profile page');
      return;
    }
    combineLatest([this.config.routePrefix$, this.crmObjectDependencies?.defaultPageUrl$ ?? of('')])
      .pipe(take(1))
      .subscribe(([routePrefix, defaultPageUrl]) => {
        let viewRoute: string | undefined;
        switch (this.objectType) {
          case 'Contact':
            viewRoute = `${routePrefix}/${PAGE_ROUTES.CONTACT.ROOT}/${PAGE_ROUTES.CONTACT.SUBROUTES.PROFILE}`;
            break;
          case 'Company':
            viewRoute = `${routePrefix}/${PAGE_ROUTES.COMPANY.ROOT}/${PAGE_ROUTES.COMPANY.SUBROUTES.PROFILE}`;
            break;
          case 'Opportunity':
            viewRoute = `${routePrefix}/${PAGE_ROUTES.OPPORTUNITY.ROOT}/${PAGE_ROUTES.OPPORTUNITY.SUBROUTES.PROFILE}`;
            break;
          case 'CustomObject':
            if (defaultPageUrl) {
              viewRoute = `${defaultPageUrl}/${PAGE_ROUTES.CUSTOM_OBJECT.SUBROUTES.PROFILE}`;
            }
            break;
        }

        if (viewRoute) {
          this.router.navigateByUrl(viewRoute.replace(':crmObjectId', objectId));
        }
      });
  }

  pipelineChanged(pipeline: Pipeline | null) {
    if (pipeline) {
      this.selectedPipeline.set(pipeline);
      this.summaryService.setPipelineId(pipeline.id);
      this.crmPipelineService.setCurrentPipelineId(pipeline.id);
      this.updatePipelineQueryParam(pipeline.id);
      this.pipelineSelected.emit(true);
      this.removeStageFilter();
    } else {
      this.crmPipelineService.setCurrentPipelineId(null);
      this.setStateForNoPipelineSelected();
    }
  }

  private removeStageFilter(): void {
    const currentFilters = this.filters();
    const stageFieldId = StandardIds.OpportunityCalculatedStageID;
    const filtersWithoutStage = currentFilters.filter((f) => f.fieldId !== stageFieldId);
    if (filtersWithoutStage.length !== currentFilters.length) {
      this.setViewFilters(filtersWithoutStage, true);
    }
  }

  setStateForNoPipelineSelected() {
    this.showBoardEmptyState.set(true);
    this.pipelineSelected.emit(false);
  }

  updatePipelineQueryParam(pipelineId: string) {
    let encodedPipelineId = null;
    if (pipelineId) {
      encodedPipelineId = encodeURIComponent(pipelineId);
    }
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { pipelineId: encodedPipelineId },
      queryParamsHandling: 'merge',
    });
  }

  protected selectionChanged(rows: Row[]): void {
    this.selectedRows = rows;
    this.selected.emit(rows);
    if (this.selectionMode()) {
      this.pickerSelectionChange.emit(this.currentPickerSelection(rows));
    }
  }

  private currentPickerSelection(rows: Row[]): CRMTableSelection {
    if (this.dataSource.state.selectAll) {
      return {
        mode: 'all',
        options: {
          filters: this.filters(),
          search: this.dataSource.state.searchTerm,
          useSelectAll: true,
          totalObjects: this.dataSource.state.totalDataMembers,
        },
      };
    }
    return { mode: 'rows', rows };
  }

  protected columnsChanged(columns: GalaxyColumnDef[]): void {
    // there's a select column at the start of every table execution with an advanced selector
    // the columns shouldn't be saved if only the select column is available
    // in the list of columns
    const columnCount = columns.filter((c) => c.id !== 'select').length;
    if (columnCount > 0) {
      this.tableStateService.setLocalStorageColumns(columns);
    }
  }

  protected searchTermChanged(searchTerm: string): void {
    this.boardSource.setSearchTerm(searchTerm);
  }

  private createSingleRowActions(objectType: ObjectType): Observable<SingleRowAction[]> {
    let typeSingleActions$: Observable<SingleRowAction[]>;

    switch (objectType) {
      case 'Contact':
        typeSingleActions$ = this.config.contact?.singleRowTableActions$ ?? of([]);
        break;
      case 'Company':
        typeSingleActions$ = this.config.company?.singleRowTableActions$ ?? of([]);
        break;
      case 'Activity':
        typeSingleActions$ = this.config.task?.singleRowTableActions$ ?? of([]);
        break;
      case 'Opportunity':
        typeSingleActions$ = this.config.opportunity?.singleRowTableActions$ ?? of([]);
        break;
      case 'CustomObject':
        typeSingleActions$ = this.crmObjectDependencies?.singleRowTableActions$ ?? of([]);
        break;
      case 'PotentialContact':
        typeSingleActions$ = this.config.potentialcontact?.singleRowTableActions$ ?? of([]);
        break;
      default:
        typeSingleActions$ = of([]);
        break;
    }

    return combineLatest([typeSingleActions$, this.singleActions$$]).pipe(
      map(([typeActions, singleActions]) => [...typeActions, ...singleActions]),
    );
  }

  private deleteRows(rows: Row[]): void {
    const deleteSubscription = combineLatest([
      this.translateForCrmObjectService.getTranslationForCrmObject(
        this.objectTypeForTranslation,
        'LIST_OBJECTS_TABLE.ACTIONS.DELETE_SELECTED.TITLE',
      ),
      this.translateForCrmObjectService.getTranslationForCrmObject(
        this.objectTypeForTranslation,
        'LIST_OBJECTS_TABLE.ACTIONS.DELETE_SELECTED.CONFIRMATION_MESSAGE',
      ),
      this.translateForCrmObjectService.getTranslationForCrmObject(
        this.objectTypeForTranslation,
        'LIST_OBJECTS_TABLE.ACTIONS.DELETE_SELECTED.CONFIRM',
      ),
      this.translateForCrmObjectService.getTranslationForCrmObject(
        this.objectTypeForTranslation,
        'LIST_OBJECTS_TABLE.ACTIONS.DELETE_SELECTED.CANCEL',
      ),
      this.translateForCrmObjectService.getTranslationForCrmObject(
        this.objectTypeForTranslation,
        'LIST_OBJECTS_TABLE.ACTIONS.DELETE_SELECTED.CONFIRMATION_TEXT',
      ),
      this.translateForCrmObjectService.getTranslationForCrmObject(
        this.objectTypeForTranslation,
        'LIST_OBJECTS_TABLE.ACTIONS.DELETE_SELECTED.INSTRUCTIONS',
      ),
    ])
      .pipe(
        switchMap(([title, message, confirmationButtonText, cancelButtonText, confirmationText, instructions]) => {
          return this.confirmationModalService.openModal({
            type: 'warn',
            title: title,
            message: message + '\n' + instructions,
            confirmButtonText: confirmationButtonText,
            cancelButtonText: cancelButtonText,
            confirmationText: confirmationText || undefined,
          });
        }),
        switchMap((confirmed) => {
          if (confirmed) {
            const objectKeys = rows.map((row) => ({ id: row.id, namespace: row.data['namespace']?.value }));
            return forkJoin(
              objectKeys.map((key) => {
                return this.service.deleteObject(key.namespace, key.id).pipe(
                  map(() => ''),
                  catchError(() => {
                    return of(key.id);
                  }),
                );
              }),
            ).pipe(
              withLatestFrom(
                this.translateForCrmObjectService.getTranslationForCrmObject(
                  this.objectTypeForTranslation,
                  'LIST_OBJECTS_TABLE.ACTIONS.DELETE_SELECTED.SUCCESS',
                ),
                this.translateForCrmObjectService.getTranslationForCrmObject(
                  this.objectTypeForTranslation,
                  'LIST_OBJECTS_TABLE.ACTIONS.DELETE_SELECTED.ERROR',
                ),
              ),
              map(([resp, success, error]: [string[], string, string]) => {
                const failedIDs = resp.filter((address) => address !== '');
                if (failedIDs.length === 0) {
                  this.snackService.openSuccessSnack(success);
                } else {
                  console.error('Failed to delete IDs: ', failedIDs);
                  this.snackService.openErrorSnack(error);
                }
                if (resp.length - failedIDs.length > 0) {
                  // If anything has been deleted, clear the selection
                  this.dataSource.clearSelection();
                }
              }),
            );
          }
          return of(null);
        }),
      )
      .subscribe();

    this.subscriptions.push(deleteSubscription);
  }

  private createMultiRowActions(objectType: ObjectType): Observable<MultiRowAction[]> {
    let typeMultiActions$: Observable<MultiRowAction[]>;

    switch (objectType) {
      case 'Contact':
        typeMultiActions$ = this.config.contact?.multiRowTableActions$ ?? of([]);
        break;
      case 'Company':
        typeMultiActions$ = this.config.company?.multiRowTableActions$ ?? of([]);
        break;
      case 'Activity':
        typeMultiActions$ = this.config.task?.multiRowTableActions$ ?? of([]);
        break;
      case 'Opportunity':
        typeMultiActions$ = this.config.opportunity?.multiRowTableActions$ ?? of([]);
        break;
      case 'CustomObject':
        typeMultiActions$ = this.crmObjectDependencies?.multiRowTableActions$ ?? of([]);
        break;
      case 'PotentialContact':
        typeMultiActions$ = this.config.potentialcontact?.multiRowTableActions$ ?? of([]);
        break;
      default:
        typeMultiActions$ = of([]);
        break;
    }

    const deleteRowsAction$ = this.showDefaultRowActions()
      ? this.translateForCrmObjectService
          .getTranslationForCrmObject(
            this.objectTypeForTranslation,
            'LIST_OBJECTS_TABLE.ACTIONS.DELETE_SELECTED.ACTION',
          )
          .pipe(
            map((label) => ({
              label: label,
              callback: (rows: Row[]) => this.deleteRows(rows),
              selectAllVisible: false,
            })),
          )
      : of(null);
    return combineLatest([typeMultiActions$, this.multiActions$$, deleteRowsAction$]).pipe(
      map(([typeActions, multiActions, deleteRowsAction]) =>
        deleteRowsAction ? [...multiActions, deleteRowsAction, ...typeActions] : [...multiActions, ...typeActions],
      ),
    );
  }

  private createSelectAllActions(objectType: ObjectType): Observable<SelectAllAction[]> {
    let selectAllActions$: Observable<SelectAllAction[]>;

    switch (objectType) {
      case 'Contact':
        selectAllActions$ = this.config.contact?.selectAllTableActions$ ?? of([]);
        break;
      case 'Company':
        selectAllActions$ = this.config.company?.selectAllTableActions$ ?? of([]);
        break;
      case 'Activity':
        selectAllActions$ = this.config.task?.selectAllTableActions$ ?? of([]);
        break;
      case 'Opportunity':
        selectAllActions$ = this.config.opportunity?.selectAllTableActions$ ?? of([]);
        break;
      case 'CustomObject':
        selectAllActions$ = this.config.customobject?.selectAllTableActions$ ?? of([]);
        break;
      case 'PotentialContact':
        selectAllActions$ = this.config.potentialcontact?.selectAllTableActions$ ?? of([]);
        break;
      default:
        selectAllActions$ = of([]);
        break;
    }
    return selectAllActions$;
  }

  protected handleSelectAll(): void {
    this.dataSource.toggleSelectAll();
    if (this.selectionMode()) {
      this.pickerSelectionChange.emit(this.currentPickerSelection(this.selectedRows));
    }
  }

  protected handleClearSelection(): void {
    this.dataSource.clearSelectionOnly();
    if (this.selectionMode()) {
      this.pickerSelectionChange.emit(this.currentPickerSelection([]));
    }
  }

  protected async changeDisplay(display: 'grid' | 'list'): Promise<void> {
    const routeDisplay = display === 'grid' ? 'board' : display;
    let routeObject;
    if (this.objectType === 'Contact') {
      routeObject = `${PAGE_ROUTES.CONTACT.ROOT}`;
    } else if (this.objectType === 'Company') {
      routeObject = `${PAGE_ROUTES.COMPANY.ROOT}`;
    } else if (this.objectType === 'Activity' && this.activityType() === 'Task') {
      routeObject = `${PAGE_ROUTES.TASK.ROOT}`;
    } else if (this.objectType === 'Opportunity') {
      routeObject = `${PAGE_ROUTES.OPPORTUNITY.ROOT}`;
    } else if (this.objectType === 'CustomObject') {
      const defaultPageUrl = await firstValueFrom(this.crmObjectDependencies?.defaultPageUrl$ ?? of(''));
      await this.router.navigateByUrl(`${defaultPageUrl}/${routeDisplay}`);
      return;
    } else {
      throw new Error(`Invalid object type: ${this.objectType}`);
    }
    const routePrefix = await firstValueFrom(this.config.routePrefix$);
    if (!routeObject) throw new Error('routeObject is undefined');
    await this.router.navigate([String(routePrefix ?? ''), String(routeObject ?? ''), String(routeDisplay ?? '')], {
      queryParamsHandling: 'merge',
    });
  }

  protected boardItemUpdateFailed(): void {
    this.snackService.openErrorSnack('ERRORS.GENERIC_EDIT_MESSAGE');
  }

  protected boardItemClicked(event: CrmBoardItemClickedEvent): void {
    this.viewObject(event.objectId);
  }

  protected handleFilterOpenChange(open: boolean): void {
    if (this.filtersToggleable()) {
      // save filter bar state to local storage
      const key = this.tableStateService.tableState().filterBarOpenStorageKey;
      if (key) {
        localStorage.setItem(key, open ? 'open' : 'closed');
      }
    }
    this.filterBarOpen.set(open);
  }

  private loadFilterBarState(): void {
    const key = this.tableStateService.tableState().filterBarOpenStorageKey;
    const stored = key ? localStorage.getItem(key) : null;
    this.filterBarOpen.set(stored ? stored !== 'closed' : null);
  }

  openAndSetupPipelineSettings(): void {
    this.config.routePrefix$.pipe(take(1)).subscribe((routePrefix) => {
      const settingPage = `${routePrefix}/${PAGE_ROUTES.PIPELINE_SETTINGS.ROOT}`;
      this.router.navigateByUrl(this.router.createUrlTree([settingPage], { queryParams: { create: true } }));
    });
  }

  /* View tab/filters code START */
  protected readonly viewTabsLoading = signal<boolean>(false);

  //input filters
  private readonly defaultViewFilters$ = toObservable(this.presetFilters);

  private loadLocallySavedStringList(key: string): string[] {
    const hiddenDefaults = localStorage.getItem(key);
    if (hiddenDefaults) {
      try {
        return JSON.parse(hiddenDefaults);
      } catch {
        localStorage.removeItem(key);
      }
    }
    return [];
  }
  //load order and visability of preset views
  private setUpViewFilterEffects() {
    effect(() => {
      const defaultsKey = this.hideDefaultViewFiltersKey();
      this.hiddenDefaultViewFilters$$.next(this.loadLocallySavedStringList(defaultsKey));
    });
    effect(() => {
      const viewOrderKey = this.viewFilterOrderKey();
      this.viewFilterOrder$$.next(this.loadLocallySavedStringList(viewOrderKey));
    });
  }

  /**
   * The chips a view gets merged into.
   * Keeps the chips to apply quick filters, otherwise they get clobbered when Ticket portal views apply
   */
  private async viewFilterSeeds(): Promise<GalaxyFilterInterface[]> {
    const initialFilters = await this.crmFiltersService.getInitialAppliedFilters$(
      this.objectType,
      this.crmObjectDependencies?.objectSubtype$,
    );
    if (initialFilters.length > 0) {
      return initialFilters;
    }
    return this.filterChipDependencies?.getInitialAppliedFilters?.() ?? initialFilters;
  }

  //change which filters apply to the table/board
  protected async applyViewFilters(filters: GalaxyFilterInterface[]): Promise<void> {
    const initialFilters = await this.viewFilterSeeds();
    const mergedFilters: GalaxyFilterInterface[] = [];
    initialFilters.forEach((filter) => {
      const fieldId = filter.fieldId;
      const values = filter.values ?? [];
      if (values.length > 0 || filter.operator !== 3) {
        //maintain defaults
        mergedFilters.push(filter);
      } else {
        const override = filters.find((filter) => filter.fieldId === fieldId);
        if (!override) {
          //override empty filter bubbles
          mergedFilters.push(filter);
        }
      }
    });
    mergedFilters.push(...filters);
    this.setViewFilters(mergedFilters, true);
  }

  //save filter changes
  protected setViewFilters(filters: GalaxyFilterInterface[], updateChipList?: boolean): void {
    this.dataSource.setFilters(filters);
    this.boardSource.setFilters(filters);
    this.filters.set(filters);
    if (updateChipList) {
      this.filterChipRef?.filters.set(filters);
    }
    this.trackingService.trackFilterUsage(this.objectType, filters);
  }

  //template to use when creating new views.
  protected readonly newViewFilterTemplate = computed(() => {
    return {
      name: '',
      filters: this.filters(), //based on current filters
      preset: false,
    } as SavedFilter;
  });

  protected readonly viewFilterKey = computed(() =>
    this.crmViewConfigService.buildViewFilterKey({
      appID: this.config.appID,
      objectType: this._objectType(),
      objectSubtype: this.crmObjectSubtype(),
      activityType: this.activityType(),
    }),
  );

  // preset views are not saved locally, we just need to save if they are hidden or not
  private readonly hideDefaultViewFiltersKey = computed(() => {
    return this.viewFilterKey() + ':hide-defaults';
  });
  private readonly hiddenDefaultViewFilters$$ = new ReplaySubject<string[]>(1);

  // order is saved locally
  private readonly viewFilterOrderKey = computed(() => {
    return this.viewFilterKey() + ':order';
  });
  protected readonly viewFilterOrder$$ = new ReplaySubject<string[]>(1);

  private sortFilters(filters: SavedFilter[]): SavedFilter[] {
    return filters.sort((a, b) => a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase()));
  }

  //reload
  private readonly refreshCloudSavedFilters$$ = new BehaviorSubject(false);
  // cloud saved filters
  private readonly cloudSavedViewFiltersWithConfig$ = combineLatest([
    this.config.namespace$,
    this.config.currentUserId$,
    toObservable(this.viewFilterKey),
    this.refreshCloudSavedFilters$$,
  ]).pipe(
    switchMap(([namespace, userId, componentKey]) =>
      namespace
        ? this.crmViewConfigService.loadSavedViewFilters(namespace, userId, componentKey)
        : of([] as SavedFilter[]),
    ),
    map((savedFilters) => this.sortFilters(savedFilters)),
    shareReplay(1),
  );

  protected readonly viewFilterTabs$ = combineLatest([
    this.defaultViewFilters$,
    this.cloudSavedViewFiltersWithConfig$,
    this.hiddenDefaultViewFilters$$,
    toObservable(this.hideCustomViews),
  ]).pipe(
    map(([presets, cloudSaved, hiddenDefaults, hideCustomViews]) => {
      //set which presets are hidden
      const presetFilters =
        presets?.map((f) => ({ ...f, preset: true, hidden: hiddenDefaults?.includes(f.id || '') }) as SavedFilter) ??
        [];

      const customFilterGroup = {
        name: this.translate.instant('VIEW_TABS.CUSTOM_VIEWS_TAB_GROUP'),
        id: 'custom',
        tabs: cloudSaved,
        canModify: true,
      } as TabGroup<SavedFilter>;

      const defaultFilterGroup = {
        name: this.translate.instant('VIEW_TABS.DEFAULT_VIEWS_TAB_GROUP'),
        id: 'default',
        tabs: presetFilters,
        canAddMore: false,
      } as TabGroup<SavedFilter>;

      const groups: TabGroup<SavedFilter>[] = presetFilters.length > 0 ? [defaultFilterGroup] : [];
      if (!hideCustomViews) {
        groups.push(customFilterGroup);
      }
      return groups;
    }),
    startWith([] as TabGroup<SavedFilter>[]),
  );

  protected async changeCurrentViewFilter($event: SavedFilter) {
    const filterCopy: GalaxyFilterInterface[] = JSON.parse(JSON.stringify($event.filters));
    await this.applyViewFilters(filterCopy);
  }

  protected async deleteViewFilters($event: string[]) {
    if ($event.length == 0) {
      return;
    }
    const namespace = await firstValueFrom(this.config.namespace$);
    const prevViewFilters = await firstValueFrom(this.cloudSavedViewFiltersWithConfig$);
    const pendingDeletes: Promise<boolean>[] = [];
    prevViewFilters.forEach((view) => {
      if (view.id && $event.includes(view.id)) {
        const pendingDelete = this.viewConfigService
          .deleteViewConfiguration({
            namespace: namespace,
            viewConfigurationId: view.id,
          })
          .pipe(
            catchError(() => of(null)),
            map((resp) => {
              return resp?.ok || false;
            }),
          );
        pendingDeletes.push(firstValueFrom(pendingDelete));
      }
    });
    const results = await Promise.all(pendingDeletes);
    const failures = results.filter((result) => !result);
    if (failures.length == results.length) {
      this.snackService.openErrorSnack('VIEW_TABS.ERRORS.FAILED_TO_DELETE_CUSTOM_VIEWS');
    } else if (failures.length > 0) {
      this.snackService.openErrorSnack('VIEW_TABS.ERRORS.PARTIAL_FAIL_TO_DELETE_CUSTOM_VIEWS');
    } else {
      this.snackService.openSuccessSnack('VIEW_TABS.SUCCESS.SAVED_CUSTOM_VIEWS');
    }

    // if we refresh right away we won't get the updates
    this.viewTabsLoading.set(true);
    setTimeout(() => {
      this.refreshCloudSavedFilters$$.next(true);
      this.viewTabsLoading.set(false);
    }, 4000);
  }

  protected async saveViewFilterTabChanges($event: TabGroup<SavedFilter>[]) {
    if ($event.length == 0) {
      return;
    }
    const namespace = await firstValueFrom(this.config.namespace$);
    const userId = await firstValueFrom(this.config.currentUserId$);
    const pendingChanges: Promise<boolean>[] = [];
    $event.forEach((group) => {
      if (group.id === 'default') {
        this.saveDefaultViewFilters(group);
      }
      if (group.id === 'custom') {
        const updates = group.tabs.map((tab) => {
          if (tab.id) {
            return firstValueFrom(
              this.viewConfigService
                .updateViewConfigurations({
                  viewConfigurationId: tab.id,
                  namespace: namespace,
                  name: tab.name,
                  filters: JSON.stringify(tab.filters),
                  enabled: !tab.hidden,
                  fieldMask: {
                    paths: ['view__filter', 'view_configuration__name', 'view_configuration__enabled'],
                  },
                })
                .pipe(
                  catchError(() => of(null)),
                  map((resp) => resp?.ok || false),
                ),
            );
          } else {
            return firstValueFrom(
              this.viewConfigService
                .createViewConfiguration({
                  namespace: namespace,
                  componentId: this.viewFilterKey(),
                  name: tab.name,
                  filters: JSON.stringify(tab.filters),
                  enabled: !tab.hidden,
                  identifier: userId,
                })
                .pipe(
                  catchError(() => of(null)),
                  map((resp) => !!resp),
                ),
            );
          }
        });
        pendingChanges.push(...updates);
      }
    });

    if (pendingChanges.length == 0) {
      return;
    }
    const results = await Promise.all(pendingChanges);
    const failures = results.filter((result) => !result);
    if (failures.length == results.length) {
      this.snackService.openErrorSnack('VIEW_TABS.ERRORS.FAILED_TO_SAVE_CUSTOM_VIEWS');
    } else if (failures.length > 0) {
      this.snackService.openErrorSnack('VIEW_TABS.ERRORS.PARTIAL_FAIL_TO_SAVE_CUSTOM_VIEWS');
    } else {
      this.snackService.openSuccessSnack('VIEW_TABS.SUCCESS.SAVED_CUSTOM_VIEWS');
    }

    // if we refresh right away we won't get the updates
    this.viewTabsLoading.set(true);
    setTimeout(() => {
      this.refreshCloudSavedFilters$$.next(true);
      this.viewTabsLoading.set(false);
    }, 4000);
  }

  private saveDefaultViewFilters(group: TabGroup<SavedFilter>) {
    const hiddenTabs = group.tabs
      .filter((tab) => tab.hidden && tab.id)
      .map((tab) => {
        return tab.id;
      })
      .filter((id) => id !== '' && id !== undefined) as string[];
    try {
      localStorage.setItem(this.hideDefaultViewFiltersKey(), JSON.stringify(hiddenTabs));
      this.hiddenDefaultViewFilters$$.next(hiddenTabs);
    } catch {
      this.snackService.openErrorSnack('VIEW_TABS.ERRORS.FAILED_TO_SAVE_DEFAULT_VIEWS');
    }
  }

  protected saveViewFilterTabOrder($event: string[]) {
    try {
      localStorage.setItem(this.viewFilterOrderKey(), JSON.stringify($event));
      this.viewFilterOrder$$.next($event);
    } catch {
      this.snackService.openErrorSnack('VIEW_TABS.ERRORS.FAILED_TO_SAVE_VIEW_ORDER');
    }
    return;
  }
  /* View tab/filters code END */
}
